import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import withRouter from "../../components/Common/withRouter";

// API
import { post, get } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// reusable components
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import logo from "../../assets/images/authentication/logo-auth.svg";
import "../../assets/scss/custom/pages/Authentication.scss";
import zapIcon from "../../assets/images/authentication/zap.svg";
import creditCardIcon from "../../assets/images/authentication/credit-card.svg";
import giftIcon from "../../assets/images/authentication/gift.svg";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";
import logoutIcon from "../../assets/images/authentication/logout.svg";

const EmailVerify = (props) => {
  document.title = "Email Verification | Omaha Food Lovers";

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const [resendLoading, setResendLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [error, setError] = useState("");

  // Check email verification status from API on mount
  useEffect(() => {
    const checkEmailVerification = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          props.router.navigate("/login");
          return;
        }

        // Get fresh user data from API
        const response = await get(endpoints.me);

        if (response.data?.success && response.data?.data) {
          const userData = response.data.data;

          // Update localStorage with fresh user data
          localStorage.setItem("user", JSON.stringify(userData));

          // Check if email is verified
          if (userData.email_verified_at) {
            toastr.success("Email verified successfully!");
            props.router.navigate("/dashboard");
          }
        }
      } catch (error) {
        console.error("Error checking email verification:", error);
        // Don't redirect on error, let user stay on verification screen
      }
    };

    checkEmailVerification();
  }, [props.router]);

  const handleResendVerification = async () => {
    setResendLoading(true);
    setError("");
    try {
      // Get user email from localStorage
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const email = user.email;

      if (!email) {
        setError("User email not found. Please login again.");
        toastr.error("User email not found. Please login again.");
        setResendLoading(false);
        return;
      }

      const response = await post(endpoints.resendVerification, {
        email: email,
      });

      if (response.data.success) {
        toastr.success(
          response.data.message || "Verification link sent successfully!",
        );
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
      toastr.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await post(endpoints.logout, {});
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      toastr.success("Logged out successfully!");
      props.router.navigate("/login");
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
      toastr.error(errorMessage);
      // Don't clear local storage or redirect on error
    } finally {
      setLogoutLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="auth-page">
        <div className="auth-page-grid">
          {/* left brand / marketing panel */}
          <div className="auth-brand-panel">
            <div className="auth-brand-panel-inner">
              <Link to="/" className="auth-logo">
                <img
                  className="auth-logo-icon"
                  src={logo}
                  alt="Omaha Food Lovers"
                />
              </Link>

              <h1 className="auth-headline">
                Earn rewards every time
                <br />
                you dine in <span className="auth-headline-accent">Omaha</span>
              </h1>

              <p className="auth-subline">
                Get 5% back at 200+ restaurants.
                <br />
                Track your dining streak. Unlock exclusive perks.
              </p>

              <ul className="auth-feature-list">
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={creditCardIcon} alt="Credit Card" />
                  </span>
                  <span>
                    Link your card once. Earn 5% back automatically at 200+
                    local restaurants.
                  </span>
                </li>
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={giftIcon} alt="Gift" />
                  </span>
                  <span>
                    Redeem points for restaurant vouchers. Your rewards stay
                    local, supporting Omaha's dining scene.
                  </span>
                </li>
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={zapIcon} alt="Zap" />
                  </span>
                  <span>
                    100% free. No subscription, no hidden fees. Just eat and
                    earn.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* right form panel */}
          <div className="auth-form-panel">
            <div className="auth-form-card">
              <div className="auth-form-header">
                <h2>Email Verification</h2>
                <p>
                  Thanks for signing up! Before getting started, could you
                  verify your email address by clicking on the link we just
                  emailed to you? If you didn't receive the email, we will
                  gladly send you another.
                </p>
              </div>

              {error ? <p className="auth-server-error">{error}</p> : null}

              <div className="auth-submit-wrap">
                <CustomButton
                  type="button"
                  variant="primary"
                  iconPosition="right"
                  icon={arrowRightIcon}
                  loading={resendLoading}
                  onClick={handleResendVerification}
                >
                  Resend Verification Link
                </CustomButton>
              </div>

              <div className="auth-submit-wrap">
                <CustomButton
                  type="button"
                  variant="secondary"
                  iconPosition="right"
                  icon={logoutIcon}
                  loading={logoutLoading}
                  onClick={handleLogout}
                >
                  Logout
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
        <p className="auth-brand-footer">
          © Omaha Food Lovers {new Date().getFullYear()} - All Rights Reserved.
        </p>
      </div>
    </React.Fragment>
  );
};

export default withRouter(EmailVerify);
